import { Dialog, Button, DialogContent, Typography, DialogTitle, DialogActions } from '@material-ui/core';
import { Formik, Form } from 'formik';
import { useContext, useState } from 'react';
import * as yup from "yup"
import { ticketContext } from 'views/Ticket/TicketContext';
import EditRequestForm from "./EditRequestForm"
import axios from 'axios';
import { SaveResponse } from 'helpers/interfaces';
import { IssueServiceType, TypeInfo } from 'helpers/enums';
import { useSnackbar } from 'notistack';
import { applicationContext } from 'helpers/services';
import moment from 'moment';


let editRequestSchema = yup.object({
    id: yup.string().default(""),

    //Delivery info
    deliveryNote: yup.string().default(''),
    invoiceDate: yup.date().nullable().default(null),
    invoiceNumber: yup.string().default('')
        .when("typeInfo", {
            is: TypeInfo.Delivery,
            then: yup.string().default("").required("Please provide invoice number"),
            otherwise: yup.string().default("")
        }),

    // Jolly
    typeInfo: yup.string().default(''),

    numberOfPackages: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.MissingPackages,
            then: yup.string().default('')
                .test('numberOfPackages', 'Too much packages are provided', val => val.length < 3)
                .required('Please provide number of packages'),
            otherwise: yup.string().default('').nullable()
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedPackages,
            then: yup.string().default('')
                .test('numberOfPackages', 'Too much packages are provided', val => val.length < 3)
                .required('Please provide number of packages'),
            otherwise: yup.string().default('').nullable()
        }),

    packageNumbers: yup.array(yup.string().default('')).default([])
        .when("issueServiceType", {
            is: IssueServiceType.MissingPackages,
            then: yup.array(yup.string().default('').required('Please provide valid package number')).default([]).min(1, 'Please provide package number'),
            otherwise: yup.array(yup.string().default('')).default([])
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedPackages,
            then: yup.array(yup.string().default('').required('Please provide valid package number')).default([]).min(1, 'Please provide package number'),
            otherwise: yup.array(yup.string().default('')).default([])
        }),

    invoiceQuantity: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than or equal to 0', (value) => !value || Number(value) >= 0).required('Please provide invoice quantity'),
            otherwise: yup.string().default('').nullable()
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide invoice quantity'),
            otherwise: yup.string().default('').nullable()
        })
        .when("issueServiceType", {
            is: IssueServiceType.DamagedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide invoice quantity'),
            otherwise: yup.string().default('')
        }),

    receivedQuantity: yup.string().default('')
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide received quantity'),
            otherwise: yup.string().default('').nullable()
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than or equal to 0', (value) => !value || Number(value) >= 0).required('Please provide received quantity'),
            otherwise: yup.string().default('').nullable()
        }),

    damagedItemQuantity: yup.string()
        .when("issueServiceType", {
            is: IssueServiceType.DamagedItems,
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required('Please provide damaged item quantity'),
            otherwise: yup.string().default('')
        })
        .default(''),

    issueServiceType: yup.string().default('')
        .when("typeInfo", {
            is: (TypeInfo.Delivery),
            then: yup.string().default('').oneOf([IssueServiceType.None, IssueServiceType.DamagedPackages, IssueServiceType.MissingPackages, IssueServiceType.OvershippedItems, IssueServiceType.MissingItems, IssueServiceType.DamagedItems], "Please choose one option"),
            otherwise: yup.string().default(IssueServiceType.None)
        }),

    productSku: yup.string().default('')
        .when("typeInfo", {
            is: (TypeInfo.Product),
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('')
        })
        .when("issueServiceType", {
            is: IssueServiceType.OvershippedItems,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('').nullable()
        })
        .when("issueServiceType", {
            is: IssueServiceType.MissingItems,
            then: yup.string().default('').required("Product is mandatory"),
            otherwise: yup.string().default('').nullable()
        }),

    hasSerialNumber: yup.boolean().default(false),
    productBrand: yup.string().default(''),
    productDescription: yup.string().default(''),
    productQuantity: yup.string().default('')
        .when("typeInfo", {
            is: (TypeInfo.Product),
            then: yup.string().default('').test('ok', 'Quantity must be greater than 0', (value) => !value || Number(value) > 0).required("We need product quantity"),
            otherwise: yup.string().default('')
        }),

    productSupplierCode: yup.string().default(''),
    productSupplierName: yup.string().default(''),
    // radio button
    hasPurchaseReceipt: yup.boolean().default(false),
    canYouRepairProductYourself: yup.boolean().default(false),
    productIsUnderWarranty: yup.boolean().default(false),
    productIsNew: yup.string().default('')
        .when("typeInfo", {
            is: (TypeInfo.Product),
            then: yup.string().default('').oneOf(["new", "used"], "Please choose one option"),
            otherwise: yup.string().default('')
        }),

    // Textfield
    serialNumber: yup.string().default('').nullable(),
    suggestedAction: yup.string().default('').nullable(),

    endCustomersHasRegisteredProduct: yup.boolean().default(false),
    endCustomerEmailAddress: yup.string().default(''),
    productQRCode: yup.string().default(""),

    purchaseCountry: yup.string().default('').nullable(),
    purchaseDate: yup.date().default(null).nullable(),
});

export type EditRequest = yup.InferType<typeof editRequestSchema>;

let EditDetails = () => {
    let { enqueueSnackbar } = useSnackbar();

    let { ticketTypes, } = useContext(applicationContext);
    let { ticket, getTicket, } = useContext(ticketContext);

    let [open, setOpen] = useState(false)


    let saveRequest = async (request: EditRequest) => {
        if (request.typeInfo === TypeInfo.Delivery) {
            if (request.issueServiceType === IssueServiceType.OvershippedItems && Number(request.invoiceQuantity) === 0) {
                if (window.confirm(`Invoice quantity should be greater than 0. Do you want to continue?`) === false) {
                    return;
                }
            }
        }

        try {
            let req = {
                ...request,
                productIsNew: request.productIsNew === "new"
            };

            await axios.post<SaveResponse>('/api/Ticket/SaveTicketTypeRequest', req);

            enqueueSnackbar("saved", { variant: "success" })
            setOpen(false);

            await getTicket();

        } catch (err) { }
    }


    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                fullWidth
                color="secondary"
                variant="contained"
            >
                Edit
            </Button>
            {/* } */}
            <Dialog maxWidth='md' fullWidth open={open}>
                <DialogTitle disableTypography>
                    <Typography variant="h4">Edit ticket details</Typography>
                </DialogTitle>

                <Formik
                    enableReinitialize
                    validationSchema={editRequestSchema}
                    initialValues={{
                        ...editRequestSchema.getDefault(),
                        id: ticket.id,
                        typeInfo: ticketTypes.getValueByKey(ticket.ticketTypeId, 'id', 'typeInfo'),
                        receivedQuantity: ticket.receivedQuantity?.toString() || '',
                        issueServiceType: ticket.issueServiceType?.toString() || '',
                        numberOfPackages: ticket.numberOfPackages?.toString() || '',
                        packageNumbers: ticket.packageNumbers || [],
                        invoiceDate: ticket.invoiceDate ? moment(ticket.invoiceDate).toDate() : null,
                        deliveryNote: ticket.deliveryNote || '',
                        invoiceQuantity: ticket.invoiceQuantity?.toString() || "",
                        invoiceNumber: ticket.invoiceNumber || '',
                        canYouRepairProductYourself: ticket.canYouRepairProductYourself || false,
                        hasPurchaseReceipt: ticket.hasPurchaseReceipt || false,
                        productIsNew: ticket.productIsNew ? "new" : "used",
                        productQuantity: ticket.productQuantity || "",
                        productSku: ticket.productSKU || '',
                        productBrand: ticket.productBrand || '',
                        productDescription: ticket.productDescription || '',
                        productSupplierCode: ticket.productSupplierCode || '',
                        productSupplierName: ticket.productSupplierName || '',
                        serialNumber: ticket.serialNumber || '',
                        suggestedAction: ticket.suggestedAction || '',
                        endCustomersHasRegisteredProduct: ticket.endCustomersHasRegisteredProduct || false,
                        endCustomerEmailAddress: ticket.endCustomerEmailAddress || '',
                        productQRCode: ticket.productQRCode || '',
                        productIsUnderWarranty: ticket.productIsUnderWarranty || false,
                        purchaseCountry: ticket.purchaseCountry || '',
                        purchaseDate: ticket.purchaseDate ? moment(ticket.purchaseDate).toDate() : null,
                        hasSerialNumber: ticket.hasSerialNumber || false,
                        damagedItemQuantity: ticket.damagedItemQuantity?.toString() || "",
                    }}
                    onSubmit={async (values, action) => {
                        await saveRequest(values);
                        action.setSubmitting(false);
                    }}
                >
                    <Form>
                        <DialogContent>
                            <EditRequestForm />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpen(false)} color="primary">
                                Cancel
                            </Button>
                            <Button type="submit" color="secondary">
                                Save
                            </Button>
                        </DialogActions>
                    </Form>
                </Formik>
            </Dialog>
        </>
    )
}

export default EditDetails;